<script setup>
  import { computed, ref } from "vue";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { useMCampaignQuotasStore } from "@stores/mCampaignQuotas";
  import { detectionLabel } from "@utils/quotaDetection";

  const props = defineProps({
    answerQuotaId: { type: [Number, String], required: true },
  });

  const mCampaignQuotasStore = useMCampaignQuotasStore();

  const answerQuota = computed(() => mCampaignQuotasStore.findAnswerQuota(props.answerQuotaId));
  const label = computed(() => detectionLabel(answerQuota.value));

  const genderOptions = [
    { value: "none", label: "None" },
    { value: "men", label: "Men" },
    { value: "women", label: "Women" },
  ];

  const editing = ref(false);
  const saving = ref(false);
  const draft = ref({});

  const startEditing = () => {
    const { gender, age_min, age_max } = answerQuota.value;
    draft.value = { gender: ["men", "women"].includes(gender) ? gender : "none", age_min, age_max };
    editing.value = true;
  };

  const toAge = (value) => (value === "" || value == null ? null : Number(value));

  const save = async () => {
    saving.value = true;
    const saved = await mCampaignQuotasStore.updateAnswerQuotaDetection(props.answerQuotaId, {
      gender: draft.value.gender === "none" ? null : draft.value.gender,
      age_min: toAge(draft.value.age_min),
      age_max: toAge(draft.value.age_max),
    });
    saving.value = false;
    if (saved) editing.value = false;
  };
</script>

<template>
  <div
    v-if="answerQuota"
    class="space-y-2 text-xs text-muted-foreground"
    data-cy="quota-detection">
    <div
      v-if="!editing"
      class="flex items-center gap-2">
      <span>{{ label ? `Detected: ${label}` : "No gender or age detected for this quota" }}</span>
      <Button
        variant="link"
        size="sm"
        class="h-auto p-0 text-xs"
        @click="startEditing">
        Edit
      </Button>
    </div>
    <div
      v-else
      class="flex items-end gap-2">
      <div class="w-28 space-y-1">
        <span>Gender</span>
        <Select
          v-model="draft.gender"
          :options="genderOptions" />
      </div>
      <div class="w-20 space-y-1">
        <span>Age min</span>
        <Input
          v-model="draft.age_min"
          type="number"
          min="0" />
      </div>
      <div class="w-20 space-y-1">
        <span>Age max</span>
        <Input
          v-model="draft.age_max"
          type="number"
          min="0" />
      </div>
      <Button
        size="sm"
        :disabled="saving"
        @click="save">
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        @click="editing = false">
        Cancel
      </Button>
    </div>
  </div>
</template>
